import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BackupService } from '../services/backupService';
import {
  ProgressArchiveService,
  ProgressArchiveListItem,
  ProgressArchiveDetail,
} from '../services/progressArchiveService';
import { progressService } from '../services/progressService';
import { useDialog } from '../context/DialogContext';
import { useStepWorkspace } from '../context/StepWorkspaceContext';
import { useLocale } from '../context/LocaleContext';
import { getActiveLocale, t as translate, type TranslateVars } from '../i18n/translate';
import styles from '../styles/stepWorkArchiveTab.module.css';

interface StepWorkArchiveTabProps {
  user?: { email?: string };
}

type Translate = (key: string, vars?: TranslateVars) => string;

interface WorkSummaryInfo {
  gettingStartedComplete: boolean | undefined;
  completedCount: number;
  hasData: boolean;
}

function defaultSaveName(t: Translate): string {
  return t('archive.default_save_name', {
    date: new Date().toLocaleDateString(getActiveLocale(), { month: 'short', day: 'numeric', year: 'numeric' }),
  });
}

function buildWorkSummary(t: Translate, info: WorkSummaryInfo | null): string {
  if (!info) {
    return '';
  }
  const { gettingStartedComplete, completedCount, hasData } = info;
  if (!hasData) {
    return t('archive.summary.no_work');
  }
  const parts: string[] = [];
  if (gettingStartedComplete) {
    parts.push(t('archive.summary.getting_started_done'));
  } else {
    parts.push(t('archive.summary.getting_started_started'));
  }
  if (completedCount > 0) {
    parts.push(t('archive.summary.steps_completed', { count: completedCount }));
  } else {
    parts.push(t('archive.summary.working_through'));
  }
  return parts.join(' · ');
}

const StepWorkArchiveTab: React.FC<StepWorkArchiveTabProps> = ({ user }) => {
  const { t } = useLocale();
  const { showAlert, showConfirm } = useDialog();
  const { notifyStepWorkspaceChanged } = useStepWorkspace();
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState<ProgressArchiveListItem[]>([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [maxArchives, setMaxArchives] = useState(5);
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [activeSummaryInfo, setActiveSummaryInfo] = useState<WorkSummaryInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState(() => defaultSaveName(t));
  const [showSaveForm, setShowSaveForm] = useState(false);

  const [viewArchive, setViewArchive] = useState<ProgressArchiveDetail | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [listResult, hasData] = await Promise.all([
        ProgressArchiveService.listArchives(),
        BackupService.hasCurrentData('SAA', '12-steps'),
      ]);
      setArchives(listResult.archives);
      setArchiveCount(listResult.count);
      setMaxArchives(listResult.max);
      setHasActiveWork(hasData);

      const gettingStarted = await progressService.getStepProgress('SAA', '12-steps', 0).catch(() => null);
      const gsComplete = !!gettingStarted?.completed;

      let completedCount = 0;
      if (hasData) {
        const allSteps = await progressService.getAllStepProgress('SAA', '12-steps');
        completedCount = Object.values(allSteps).filter((s) => s?.completed).length;
      }
      setActiveSummaryInfo({ gettingStartedComplete: gsComplete, completedCount, hasData });
    } catch (err) {
      console.error('Error loading archives:', err);
      setError(translate('archive.error.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeSummary = buildWorkSummary(t, activeSummaryInfo);
  const atArchiveLimit = archiveCount >= maxArchives;
  const userArchives = archives.filter((a) => !a.is_auto_archive);
  const autoArchives = archives.filter((a) => a.is_auto_archive);

  const openSaveForm = () => {
    if (!hasActiveWork) {
      showAlert({
        title: t('archive.dialog.nothing_to_save_title'),
        message: t('archive.dialog.nothing_to_save_message'),
      });
      return;
    }
    if (atArchiveLimit) {
      showAlert({
        title: t('archive.dialog.limit_title'),
        message: t('archive.dialog.limit_message', { max: maxArchives }),
      });
      return;
    }
    setSaveName(defaultSaveName(t));
    setShowSaveForm(true);
  };

  const handleStartOver = async () => {
    const name = saveName.trim();
    if (!name) {
      setError(t('archive.error.name_required'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ProgressArchiveService.createArchive({
        archive_name: name,
        clear_active: true,
      });
      setShowSaveForm(false);
      await loadData();
      notifyStepWorkspaceChanged();
      showAlert({
        title: t('archive.dialog.fresh_start_title'),
        message: t('archive.dialog.fresh_start_message', { name }),
        variant: 'success',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('archive.error.save_failed');
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleViewArchive = async (archiveId: string) => {
    setViewLoading(true);
    setViewArchive(null);
    try {
      const detail = await ProgressArchiveService.getArchive(archiveId);
      setViewArchive(detail);
    } catch (err) {
      showAlert({
        title: t('archive.dialog.open_failed_title'),
        message: err instanceof Error ? err.message : t('archive.dialog.open_failed_message'),
        variant: 'danger',
      });
    } finally {
      setViewLoading(false);
    }
  };

  const handleRestoreArchive = (item: ProgressArchiveListItem) => {
    showConfirm({
      title: t('archive.dialog.restore_title'),
      message: t('archive.dialog.restore_message', { name: item.archive_name }),
      confirmText: t('archive.dialog.restore_confirm'),
      variant: 'warning',
      onConfirm: async () => {
        setBusy(true);
        setError(null);
        try {
          await ProgressArchiveService.restoreArchive(item.archive_id);
          await loadData();
          notifyStepWorkspaceChanged();
          showAlert({
            title: t('archive.dialog.restored_title'),
            message: t('archive.dialog.restored_message', { name: item.archive_name }),
            variant: 'success',
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : t('archive.error.restore_failed');
          setError(message);
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleDeleteArchive = (item: ProgressArchiveListItem) => {
    showConfirm({
      title: t('archive.dialog.delete_title'),
      message: t('archive.dialog.delete_message', { name: item.archive_name }),
      confirmText: t('dialog.delete'),
      variant: 'danger',
      onConfirm: async () => {
        setBusy(true);
        setError(null);
        try {
          await ProgressArchiveService.deleteArchive(item.archive_id);
          if (viewArchive?.archive_id === item.archive_id) {
            setViewArchive(null);
          }
          await loadData();
        } catch (err) {
          const message = err instanceof Error ? err.message : t('archive.error.delete_failed');
          setError(message);
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const renderArchiveRow = (item: ProgressArchiveListItem) => (
    <li key={item.archive_id} className={styles.archiveItem}>
      <div className={styles.archiveInfo}>
        <div className={styles.archiveName}>
          {item.archive_name}
          {item.is_auto_archive && <span className={styles.autoTag}>{t('archive.list.auto_tag')}</span>}
        </div>
        <div className={styles.archiveMeta}>
          {ProgressArchiveService.formatDate(item.created_at)} ·{' '}
          {ProgressArchiveService.formatSummary(item.summary)}
        </div>
      </div>
      <div className={styles.archiveActions}>
        <button type="button" className={styles.linkButton} onClick={() => handleViewArchive(item.archive_id)} disabled={busy}>
          {t('archive.list.details')}
        </button>
        <button type="button" className={styles.linkButton} onClick={() => handleRestoreArchive(item)} disabled={busy}>
          {t('archive.list.switch')}
        </button>
        <button type="button" className={styles.linkButtonDanger} onClick={() => handleDeleteArchive(item)} disabled={busy}>
          {t('dialog.delete')}
        </button>
      </div>
    </li>
  );

  if (!user) {
    return (
      <div className={styles.emptyState}>
        <p>{t('archive.signed_out')}</p>
      </div>
    );
  }

  if (loading) {
    return <div className={styles.loading}>{t('archive.loading')}</div>;
  }

  return (
    <div className={styles.container}>
      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.hero}>
        <h3 className={styles.heroTitle}>{t('archive.hero.title')}</h3>
        <p className={styles.heroLead}>{t('archive.hero.lead')}</p>

        {hasActiveWork ? (
          <>
            <div className={styles.currentWork}>
              <span className={styles.currentWorkLabel}>{t('archive.hero.current_work')}</span>
              <span className={styles.currentWorkSummary}>{activeSummary}</span>
            </div>

            {!showSaveForm ? (
              <button
                type="button"
                className={styles.heroButton}
                onClick={openSaveForm}
                disabled={busy || atArchiveLimit}
              >
                {t('archive.hero.save_and_start_over')}
              </button>
            ) : (
              <div className={styles.saveForm}>
                <label className={styles.label} htmlFor="save-name">
                  {t('archive.form.name_label')}
                </label>
                <p className={styles.labelHint}>{t('archive.form.name_hint')}</p>
                <input
                  id="save-name"
                  type="text"
                  className={styles.textInput}
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  maxLength={120}
                  disabled={busy}
                />
                <ul className={styles.whatHappens}>
                  <li>{t('archive.form.what_happens_save')}</li>
                  <li>{t('archive.form.what_happens_reset')}</li>
                  <li>{t('archive.form.what_happens_switch_back')}</li>
                </ul>
                <div className={styles.saveFormActions}>
                  <button
                    type="button"
                    className={styles.heroButton}
                    onClick={handleStartOver}
                    disabled={busy}
                  >
                    {busy ? t('archive.form.saving') : t('archive.hero.save_and_start_over')}
                  </button>
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => setShowSaveForm(false)}
                    disabled={busy}
                  >
                    {t('dialog.cancel')}
                  </button>
                </div>
              </div>
            )}

            {atArchiveLimit && !showSaveForm && (
              <p className={styles.heroHint}>
                {t('archive.hero.limit_hint', { max: maxArchives })}
              </p>
            )}
          </>
        ) : (
          <div className={styles.readyBlock}>
            <p className={styles.readyMessage}>
              {archives.length > 0
                ? t('archive.ready.with_archives')
                : t('archive.ready.no_archives')}
            </p>
            <Link to="/saa/12-steps/getting-started" className={styles.heroButton}>
              {t('archive.ready.go_getting_started')}
            </Link>
          </div>
        )}
      </section>

      {archives.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>{t('archive.list.title')}</h4>
          <p className={styles.sectionIntro}>
            {hasActiveWork
              ? t('archive.list.intro_active')
              : t('archive.list.intro_ready')}
          </p>

          {userArchives.length > 0 && (
            <ul className={styles.archiveList}>{userArchives.map(renderArchiveRow)}</ul>
          )}

          {autoArchives.length > 0 && (
            <>
              {userArchives.length > 0 && (
                <p className={styles.autoSectionLabel}>{t('archive.list.auto_section')}</p>
              )}
              <ul className={styles.archiveList}>{autoArchives.map(renderArchiveRow)}</ul>
            </>
          )}

          <p className={styles.limitNote}>
            {t('archive.list.limit_note', { max: maxArchives, count: archiveCount })}
          </p>
        </section>
      )}

      {(viewLoading || viewArchive) && (
        <div className={styles.viewOverlay} onClick={() => !viewLoading && setViewArchive(null)}>
          <div className={styles.viewPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.viewHeader}>
              <h4>{viewArchive?.archive_name || t('archive.view.default_title')}</h4>
              <button type="button" className={styles.closeView} onClick={() => setViewArchive(null)} aria-label={t('dialog.close')}>
                ×
              </button>
            </div>
            {viewLoading ? (
              <p className={styles.viewBody}>{t('archive.loading')}</p>
            ) : viewArchive ? (
              <div className={styles.viewBody}>
                <p className={styles.viewMeta}>
                  {t('archive.view.saved_meta', {
                    date: ProgressArchiveService.formatDate(viewArchive.created_at),
                    summary: ProgressArchiveService.formatSummary(viewArchive.summary),
                  })}
                </p>
                {viewArchive.summary?.completed_step_numbers?.length ? (
                  <p>{t('archive.view.completed_steps', { steps: viewArchive.summary.completed_step_numbers.join(', ') })}</p>
                ) : null}
                {viewArchive.summary?.has_inventories && (
                  <p>{t('archive.view.inventories_included', { count: viewArchive.summary.inventory_count })}</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepWorkArchiveTab;
